/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useCallback, useEffect, useRef } from 'react';

import { buildCommandIntelligenceOpenCacheKey, parsePersistedOpen } from './drawerLayout';
import { readCachedDrawerPreference } from './drawerPreferences';
import { cacheItem } from '../../util/cache';

export const useCommandIntelligencePersistedOpenState = ({
  enabled,
  isEntitled,
  isContextReady,
  drawerPrefsCacheKey,
  sessionOpen,
  openBlank,
  clearContext,
  close,
}) => {
  const drawerPrefsCacheKeyRef = useRef(drawerPrefsCacheKey);
  const previousDrawerPrefsCacheKeyRef = useRef(null);
  const restoredPrefsCacheKeyRef = useRef(null);
  const restoredOpenTargetRef = useRef(null);
  const openRestoreCompleteRef = useRef(false);

  drawerPrefsCacheKeyRef.current = drawerPrefsCacheKey;

  useEffect(() => {
    restoredPrefsCacheKeyRef.current = null;
    restoredOpenTargetRef.current = null;
    openRestoreCompleteRef.current = false;
  }, [drawerPrefsCacheKey]);

  useEffect(() => {
    if (!drawerPrefsCacheKey || !enabled || !isEntitled || !isContextReady) {
      return undefined;
    }

    if (restoredPrefsCacheKeyRef.current === drawerPrefsCacheKey) {
      return undefined;
    }

    restoredPrefsCacheKeyRef.current = drawerPrefsCacheKey;

    const shouldOpen = parsePersistedOpen(
      readCachedDrawerPreference(buildCommandIntelligenceOpenCacheKey(drawerPrefsCacheKey))
    );

    const isScopeChange =
      previousDrawerPrefsCacheKeyRef.current !== null && previousDrawerPrefsCacheKeyRef.current !== drawerPrefsCacheKey;

    previousDrawerPrefsCacheKeyRef.current = drawerPrefsCacheKey;
    restoredOpenTargetRef.current = shouldOpen;
    openRestoreCompleteRef.current = true;

    if (isScopeChange) {
      clearContext?.();
    }

    if (shouldOpen) {
      if (!sessionOpen || isScopeChange) {
        openBlank?.();
      }
    } else if (sessionOpen && isScopeChange) {
      close?.();
    } else if (sessionOpen) {
      restoredOpenTargetRef.current = true;
    }

    return undefined;
  }, [clearContext, close, drawerPrefsCacheKey, enabled, isContextReady, isEntitled, openBlank, sessionOpen]);

  useEffect(() => {
    if (!drawerPrefsCacheKey || !enabled || !isContextReady || !openRestoreCompleteRef.current) {
      return undefined;
    }

    if (restoredOpenTargetRef.current !== null && sessionOpen !== restoredOpenTargetRef.current) {
      return undefined;
    }

    restoredOpenTargetRef.current = null;

    cacheItem(buildCommandIntelligenceOpenCacheKey(drawerPrefsCacheKey), String(Boolean(sessionOpen)));

    return undefined;
  }, [drawerPrefsCacheKey, enabled, isContextReady, sessionOpen]);

  const persistClosed = useCallback(() => {
    const cacheKey = drawerPrefsCacheKeyRef.current;

    if (!cacheKey) {
      return;
    }

    restoredOpenTargetRef.current = null;
    cacheItem(buildCommandIntelligenceOpenCacheKey(cacheKey), 'false');
  }, []);

  return { persistClosed };
};
